import React, { useEffect } from "react";
import ModernCTA from '../../components/ModernCTA';

const WebReDevelopment = () => {
  useEffect(() => {
    // Add hover effects after component mounts
    const featureCards = document.querySelectorAll('[data-feature-card]');
    featureCards.forEach(card => {
      card.addEventListener('mouseenter', () => {
        card.style.transform = 'translateY(-8px)';
        card.style.boxShadow = '0 25px 50px -12px rgba(59, 130, 246, 0.25)';
      });
      card.addEventListener('mouseleave', () => {
        card.style.transform = '';
        card.style.boxShadow = '';
      });
    });

    const processSteps = document.querySelectorAll('[data-process-step]');
    processSteps.forEach(step => {
      step.addEventListener('mouseenter', () => {
        step.style.transform = 'translateY(-5px)';
        step.style.boxShadow = '0 20px 25px -5px rgba(59, 130, 246, 0.2)';
      });
      step.addEventListener('mouseleave', () => {
        step.style.transform = '';
        step.style.boxShadow = '';
      });
    });

    return () => {
      // Clean up event listeners
      featureCards.forEach(card => {
        card.removeEventListener('mouseenter', () => {});
        card.removeEventListener('mouseleave', () => {});
      });
      processSteps.forEach(step => {
        step.removeEventListener('mouseenter', () => {});
        step.removeEventListener('mouseleave', () => {});
      });
    };
  }, []);

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>
            <span style={styles.gradientText}>Web Re-Development</span>
          </h1>
          <p style={styles.heroSubtitle}>
            Modernize legacy systems with microservices architecture and cloud-native solutions
          </p>
          <div style={styles.animatedUnderline}></div>
          <div style={styles.scrollIndicator}>
            <div style={styles.scrollAnimation}></div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <section style={styles.section}>
        <div style={styles.content}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.gradientText}>Transform Your Digital Infrastructure</span>
          </h2>
          <p style={styles.sectionText}>
            In today's rapidly evolving digital landscape, legacy systems can become bottlenecks to growth. Our comprehensive web re-development service transforms outdated architectures into modern, scalable solutions.
          </p>
          
          <div style={styles.statsContainer}>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>3-5x</div>
              <div style={styles.statLabel}>Performance Improvement</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>70%</div>
              <div style={styles.statLabel}>Cost Reduction</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>100%</div>
              <div style={styles.statLabel}>Cloud Ready</div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Approach - 3 Cards */}
      <section style={styles.darkSection}>
        <div style={styles.content}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.gradientText}>Our Technical Methodology</span>
          </h2>
          <p style={styles.sectionText}>
            We employ a systematic approach to ensure seamless transition from legacy to modern architectures
          </p>
          
          <div style={styles.technicalGrid}>
            <div style={styles.technicalCard}>
              <h3 style={styles.technicalTitle}>Architecture Assessment</h3>
              <ul style={styles.technicalList}>
                <li>Comprehensive system audit</li>
                <li>Performance benchmarking</li>
                <li>Security analysis</li>
                <li>Scalability evaluation</li>
              </ul>
            </div>
            
            <div style={styles.technicalCard}>
              <h3 style={styles.technicalTitle}>Strategic Planning</h3>
              <ul style={styles.technicalList}>
                <li>Microservices decomposition</li>
                <li>Cloud migration roadmap</li>
                <li>CI/CD pipeline design</li>
                <li>Data migration strategy</li>
              </ul>
            </div>
            
            <div style={styles.technicalCard}>
              <h3 style={styles.technicalTitle}>Implementation</h3>
              <ul style={styles.technicalList}>
                <li>Phased migration approach</li>
                <li>Blue-green deployment</li>
                <li>Automated testing</li>
                <li>Performance optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack - 6 Cards (2x3 grid) */}
      <section style={styles.section}>
        <div style={styles.content}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.gradientText}>Modern Technology Stack</span>
          </h2>
          <p style={styles.sectionText}>
            We leverage cutting-edge technologies to build robust, future-proof solutions
          </p>
          
          <div style={styles.techStackGrid}>
            <div style={styles.techStackCard}>
              <h3 style={styles.techStackHeader}>Frontend</h3>
              <ul style={styles.techStackList}>
                <li>React.js/Next.js</li>
                <li>Vue.js/Nuxt.js</li>
                <li>TypeScript</li>
                <li>Tailwind CSS</li>
              </ul>
            </div>
            
            <div style={styles.techStackCard}>
              <h3 style={styles.techStackHeader}>Backend</h3>
              <ul style={styles.techStackList}>
                <li>Node.js/Express</li>
                <li>Python/Django</li>
                <li>Java/Spring Boot</li>
                <li>.NET Core</li>
              </ul>
            </div>
            
            <div style={styles.techStackCard}>
              <h3 style={styles.techStackHeader}>Mobile</h3>
              <ul style={styles.techStackList}>
                <li>React Native</li>
                <li>Flutter</li>
                <li>SwiftUI</li>
                <li>Kotlin</li>
              </ul>
            </div>
            
            <div style={styles.techStackCard}>
              <h3 style={styles.techStackHeader}>Database</h3>
              <ul style={styles.techStackList}>
                <li>PostgreSQL</li>
                <li>MongoDB</li>
                <li>Redis</li>
                <li>Firebase</li>
              </ul>
            </div>
            
            <div style={styles.techStackCard}>
              <h3 style={styles.techStackHeader}>Cloud</h3>
              <ul style={styles.techStackList}>
                <li>AWS</li>
                <li>Azure</li>
                <li>Google Cloud</li>
                <li>Docker/K8s</li>
              </ul>
            </div>
            
            <div style={styles.techStackCard}>
              <h3 style={styles.techStackHeader}>DevOps</h3>
              <ul style={styles.techStackList}>
                <li>Terraform</li>
                <li>GitHub Actions</li>
                <li>Prometheus</li>
                <li>Grafana</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - 6 Cards (2x3 grid) */}
      <section style={styles.darkSection}>
        <div style={styles.content}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.gradientText}>Comprehensive Features</span>
          </h2>
          
          <div style={styles.featuresGrid}>
            <div data-feature-card style={styles.featureCard}>
              <h3 style={styles.featureTitle}>Microservices</h3>
              <p style={styles.featureText}>
                Decompose monoliths into independently deployable services
              </p>
            </div>
            
            <div data-feature-card style={styles.featureCard}>
              <h3 style={styles.featureTitle}>Cloud-Native</h3>
              <p style={styles.featureText}>
                Build applications designed for cloud environments
              </p>
            </div>
            
            <div data-feature-card style={styles.featureCard}>
              <h3 style={styles.featureTitle}>API-First</h3>
              <p style={styles.featureText}>
                Design systems with clean, well-documented APIs
              </p>
            </div>
            
            <div data-feature-card style={styles.featureCard}>
              <h3 style={styles.featureTitle}>CI/CD</h3>
              <p style={styles.featureText}>
                Automated testing and deployment pipelines
              </p>
            </div>
            
            <div data-feature-card style={styles.featureCard}>
              <h3 style={styles.featureTitle}>Security</h3>
              <p style={styles.featureText}>
                Built-in security at every layer
              </p>
            </div>
            
            <div data-feature-card style={styles.featureCard}>
              <h3 style={styles.featureTitle}>Monitoring</h3>
              <p style={styles.featureText}>
                Real-time performance monitoring
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section - 6 Cards (2x3 grid) */}
      <section style={styles.processSection}>
        <div style={styles.content}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.gradientText}>Our 6-Phase Process</span>
          </h2>
          
          <div style={styles.processGrid}>
            <div data-process-step style={styles.processCard}>
              <div style={styles.processNumber}>1</div>
              <h3 style={styles.processTitle}>Discovery</h3>
              <p style={styles.processText}>
                Comprehensive assessment of current system
              </p>
            </div>
            
            <div data-process-step style={styles.processCard}>
              <div style={styles.processNumber}>2</div>
              <h3 style={styles.processTitle}>Planning</h3>
              <p style={styles.processText}>
                Creating detailed technical specifications
              </p>
            </div>
            
            <div data-process-step style={styles.processCard}>
              <div style={styles.processNumber}>3</div>
              <h3 style={styles.processTitle}>Prototype</h3>
              <p style={styles.processText}>
                Building small-scale proof of concept
              </p>
            </div>
            
            <div data-process-step style={styles.processCard}>
              <div style={styles.processNumber}>4</div>
              <h3 style={styles.processTitle}>Migration</h3>
              <p style={styles.processText}>
                Phased implementation with CI/CD
              </p>
            </div>
            
            <div data-process-step style={styles.processCard}>
              <div style={styles.processNumber}>5</div>
              <h3 style={styles.processTitle}>Testing</h3>
              <p style={styles.processText}>
                Comprehensive automated testing
              </p>
            </div>
            
            <div data-process-step style={styles.processCard}>
              <div style={styles.processNumber}>6</div>
              <h3 style={styles.processTitle}>Launch</h3>
              <p style={styles.processText}>
                Final deployment and optimization
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={styles.ctaSection}>
        <ModernCTA />
      </section>
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    color: "#e2e8f0",
    lineHeight: 1.6,
    maxWidth: "100%",
    overflowX: "hidden",
    background: "#0f172a",
  },
  hero: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "1rem",
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
  },
  heroContent: {
    maxWidth: "900px",
    margin: "0 auto",
    position: "relative",
    zIndex: 2,
    padding: "1rem",
  },
  heroTitle: {
    fontSize: "clamp(2.5rem, 5vw, 4.5rem)",
    fontWeight: 800,
    marginBottom: "1.5rem",
    lineHeight: 1.2,
    animation: "fadeInUp 0.8s ease-out",
    padding: "0 1rem",
  },
  heroSubtitle: {
    fontSize: "clamp(1.1rem, 2vw, 1.75rem)",
    fontWeight: 400,
    marginBottom: "2rem",
    color: "#94a3b8",
    animation: "fadeInUp 0.8s ease-out 0.2s both",
    padding: "0 1rem",
  },
  gradientText: {
    background: "linear-gradient(90deg, #38bdf8 0%, #818cf8 50%, #c084fc 100%)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
    display: "inline-block",
  },
  animatedUnderline: {
    height: "4px",
    width: "120px",
    background: "linear-gradient(90deg, #38bdf8 0%, #818cf8 50%, #c084fc 100%)",
    margin: "2rem auto",
    borderRadius: "2px",
    animation: "scaleIn 0.5s ease-out 0.4s both",
  },
  scrollIndicator: {
    position: "absolute",
    bottom: "40px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "30px",
    height: "50px",
    border: "2px solid #64748b",
    borderRadius: "15px",
    overflow: "hidden",
  },
  scrollAnimation: {
    position: "absolute",
    top: "10px",
    left: "50%",
    width: "4px",
    height: "10px",
    background: "#38bdf8",
    borderRadius: "2px",
    transform: "translateX(-50%)",
    animation: "scrollBounce 2s infinite",
  },
  section: {
    padding: "6rem 1rem",
    maxWidth: "1400px",
    margin: "0 auto",
  },
  darkSection: {
    padding: "6rem 1rem",
    maxWidth: "1400px",
    margin: "0 auto",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 1rem",
  },
  sectionTitle: {
    fontSize: "clamp(2rem, 4vw, 3rem)",
    fontWeight: 700,
    marginBottom: "2rem",
    lineHeight: 1.3,
    textAlign: "center",
  },
  sectionText: {
    fontSize: "clamp(1rem, 1.5vw, 1.25rem)",
    color: "#94a3b8",
    marginBottom: "3rem",
    textAlign: "center",
    maxWidth: "800px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  statsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "2rem",
    marginTop: "3rem",
    flexWrap: "wrap",
  },
  statItem: {
    textAlign: "center",
    minWidth: "120px",
    flex: "1",
  },
  statNumber: {
    fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
    fontWeight: 700,
    background: "linear-gradient(90deg, #38bdf8 0%, #818cf8 50%, #c084fc 100%)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
    marginBottom: "0.5rem",
  },
  statLabel: {
    fontSize: "clamp(0.9rem, 1.2vw, 1rem)",
    color: "#94a3b8",
    fontWeight: 500,
  },
  technicalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "2rem",
    marginTop: "3rem",
  },
  technicalCard: {
    background: "rgba(30, 41, 59, 0.5)",
    borderRadius: "12px",
    padding: "2rem",
    border: "1px solid rgba(100, 116, 139, 0.2)",
    transition: "all 0.3s ease",
  },
  technicalTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: "1.5rem",
    color: "#e2e8f0",
  },
  technicalList: {
    color: "#94a3b8",
    paddingLeft: "1.5rem",
    lineHeight: 1.8,
    listStyleType: "disc",
  },
  techStackGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "2rem",
    marginTop: "3rem",
  },
  techStackCard: {
    background: "rgba(30, 41, 59, 0.5)",
    borderRadius: "12px",
    padding: "2rem",
    border: "1px solid rgba(100, 116, 139, 0.2)",
  },
  techStackHeader: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: "1.5rem",
    color: "#e2e8f0",
    borderBottom: "2px solid #334155",
    paddingBottom: "0.5rem",
  },
  techStackList: {
    color: "#94a3b8",
    lineHeight: 1.8,
    listStyleType: "disc",
    paddingLeft: "1.5rem",
  },
  featuresGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "2rem",
    marginTop: "3rem",
  },
  featureCard: {
    background: "rgba(30, 41, 59, 0.7)",
    borderRadius: "12px",
    padding: "2rem",
    border: "1px solid rgba(100, 116, 139, 0.3)",
    transition: "all 0.3s ease",
    minHeight: "200px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  featureTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: "1rem",
    color: "#e2e8f0",
  },
  featureText: {
    color: "#94a3b8",
    fontSize: "1.1rem",
  },
  processSection: {
    padding: "6rem 1rem",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
  },
  processGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "2rem",
    marginTop: "3rem",
  },
  processCard: {
    background: "rgba(30, 41, 59, 0.7)",
    borderRadius: "12px",
    padding: "2rem",
    border: "1px solid rgba(100, 116, 139, 0.3)",
    position: "relative",
    transition: "all 0.3s ease",
    minHeight: "200px",
  },
  processNumber: {
    position: "absolute",
    top: "-15px",
    left: "-15px",
    width: "50px",
    height: "50px",
    background: "linear-gradient(135deg, #38bdf8 0%, #818cf8 100%)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0f172a",
    fontSize: "1.2rem",
    fontWeight: 700,
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  },
  processTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: "1rem",
    color: "#e2e8f0",
  },
  processText: {
    color: "#94a3b8",
    fontSize: "1.1rem",
  },
  ctaSection: {
    padding: "6rem 1rem",
    textAlign: "center",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
  },
  // Animation keyframes
  "@keyframes fadeInUp": {
    from: {
      opacity: 0,
      transform: "translateY(20px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  "@keyframes scaleIn": {
    from: {
      transform: "scaleX(0)",
    },
    to: {
      transform: "scaleX(1)",
    },
  },
  "@keyframes scrollBounce": {
    "0%": {
      transform: "translateY(-10px) translateX(-50%)",
      opacity: 0,
    },
    "50%": {
      transform: "translateY(10px) translateX(-50%)",
      opacity: 1,
    },
    "100%": {
      transform: "translateY(20px) translateX(-50%)",
      opacity: 0,
    },
  },
  // Media queries for mobile responsiveness
  "@media (max-width: 768px)": {
    hero: {
      minHeight: "90vh",
      padding: "1rem",
    },
    heroContent: {
      padding: "1rem",
    },
    section: {
      padding: "4rem 1rem",
    },
    darkSection: {
      padding: "4rem 1rem",
    },
    statsContainer: {
      gap: "1rem",
    },
    statItem: {
      minWidth: "100px",
    },
    technicalGrid: {
      gridTemplateColumns: "1fr",
    },
    techStackGrid: {
      gridTemplateColumns: "1fr",
    },
    featuresGrid: {
      gridTemplateColumns: "1fr",
    },
    processGrid: {
      gridTemplateColumns: "1fr",
    },
    ctaSection: {
      padding: "4rem 1rem",
    },
  },
  "@media (max-width: 480px)": {
    heroTitle: {
      fontSize: "2.2rem",
    },
    heroSubtitle: {
      fontSize: "1.1rem",
    },
    statItem: {
      minWidth: "80px",
    },
    statNumber: {
      fontSize: "1.5rem",
    },
    statLabel: {
      fontSize: "0.8rem",
    },
  },
};

export default WebReDevelopment;